import { DimensionUnit } from '@atlas/types'
import { Dropdown, type DropdownItem, Input, PillTab, PillTabs } from '@atlas/ui-kit'
import type { Key, ReactNode } from 'react'
import { DetailsCard, FieldCaption } from './DetailsCard'

/** The deliverable's values, as the form holds them. */
export interface DeliverableValues {
	assetTypeCode: string | null
	platform: string | null
	aspectRatio: string | null
	exactDimensions: string
	dimensionUnit: DimensionUnit | null
}

const UNIT_LABEL: Readonly<Record<DimensionUnit, string>> = {
	[DimensionUnit.PIXELS]: 'Pixels',
	[DimensionUnit.INCHES]: 'Inches',
}

const keyOrNull = (key: Key | null): string | null => (key === null ? null : String(key))

function Choice({
	label,
	items,
	value,
	onChange,
	isRequired,
	placeholder,
}: {
	label: string
	items: DropdownItem[]
	value: string | null
	/** Absent when the value is fixed, by Workfront or by the reader's rights. */
	onChange?: (value: string | null) => void
	isRequired?: boolean
	placeholder: string
}) {
	return (
		<div className="flex min-w-0 flex-1 flex-col gap-1">
			<FieldCaption label={label} isRequired={isRequired} />
			<Dropdown
				size="l"
				aria-label={label}
				items={items}
				selectedKey={value}
				onSelectionChange={(key) => onChange?.(keyOrNull(key))}
				isDisabled={!onChange}
				placeholder={placeholder}
			/>
		</div>
	)
}

/**
 * Deliverable Details: what to produce and where it runs. One order is one deliverable, so the
 * card says so; the production instructions are files, uploaded in the slot the caller passes.
 */
export function DeliverableDetailsCard({
	assetTypes,
	platforms,
	aspectRatios,
	value,
	onChange,
	locked = {},
	required = {},
	requiresAction,
	instructions,
}: {
	assetTypes: DropdownItem[]
	platforms: DropdownItem[]
	aspectRatios: DropdownItem[]
	value: DeliverableValues
	/** Absent when the whole card is read-only. */
	onChange?: (patch: Partial<DeliverableValues>) => void
	/** The values Workfront has set on a synced order, shown but not editable. */
	locked?: { assetType?: boolean; platform?: boolean }
	required?: { assetType?: boolean; platform?: boolean }
	requiresAction?: boolean
	instructions: ReactNode
}) {
	const edit = <K extends keyof DeliverableValues>(key: K, isLocked = false) =>
		onChange && !isLocked ? (next: DeliverableValues[K]) => onChange({ [key]: next }) : undefined
	const onDimensions = edit('exactDimensions')
	const onUnit = edit('dimensionUnit')
	return (
		<DetailsCard title="Deliverable Details" requiresAction={requiresAction}>
			<div className="flex flex-col gap-4 sm:flex-row">
				<Choice
					label="Deliverable/asset type"
					items={assetTypes}
					value={value.assetTypeCode}
					onChange={edit('assetTypeCode', locked.assetType)}
					isRequired={required.assetType}
					placeholder="Choose an asset type"
				/>
				<Choice
					label="Application platform/placement"
					items={platforms}
					value={value.platform}
					onChange={edit('platform', locked.platform)}
					isRequired={required.platform}
					placeholder="Choose a platform"
				/>
			</div>
			<p className="text-atlas-label text-atlas-light-dark">
				One Factory Asset = one deliverable. Other formats need their own Work Order.
			</p>
			<div className="flex flex-col gap-4 sm:flex-row">
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<FieldCaption label="Exact Dimensions" />
					<Input
						aria-label="Exact Dimensions"
						value={value.exactDimensions}
						readOnly={!onDimensions}
						status="completed"
						placeholder="e.g. 600 x auto"
						onChange={(event) => onDimensions?.(event.target.value)}
					/>
				</div>
				<Choice
					label="Aspect ratio"
					items={aspectRatios}
					value={value.aspectRatio}
					onChange={edit('aspectRatio')}
					placeholder="e.g. 1:1"
				/>
			</div>
			<div className="flex flex-col gap-1">
				<FieldCaption label="Unit" />
				<PillTabs
					aria-label="Unit"
					disallowEmptySelection={false}
					isDisabled={!onUnit}
					selectedKeys={value.dimensionUnit ? [value.dimensionUnit] : []}
					onSelectionChange={(keys) => onUnit?.(([...keys][0] as DimensionUnit | undefined) ?? null)}
				>
					{Object.values(DimensionUnit).map((unit) => (
						<PillTab key={unit} id={unit}>
							{UNIT_LABEL[unit]}
						</PillTab>
					))}
				</PillTabs>
			</div>
			<div className="flex flex-col gap-2">
				<FieldCaption label="Specific production instructions" />
				{instructions}
			</div>
		</DetailsCard>
	)
}
